(() => {
  const headingSelector = 'h1,h2,h3,h4,h5,h6,.rich-text__heading';
  const pending = new Set();
  let observer;
  let frame;
  let segmenter;
  try {
    if (typeof Intl.Segmenter === 'function') {
      segmenter = new Intl.Segmenter(document.documentElement.lang || 'en', {granularity:'sentence'});
    }
  } catch (_) { /* Use the punctuation fallback when the locale is unavailable. */ }

  function sentenceStarts(text, sentenceSegmenter = segmenter) {
    let candidates;
    if (sentenceSegmenter) {
      candidates = [...sentenceSegmenter.segment(text)].slice(1).map(item => item.index + (item.segment.match(/^\s*/)?.[0].length || 0));
    } else {
      candidates = [...text.matchAll(/[.!?](?:[”’"'»\)\]]*)\s+(?=\S)/gu)].map(match => match.index + match[0].length);
    }
    return candidates.filter(index => {
      const before = text.slice(0,index).trimEnd();
      const punctuation = before.replace(/[”’"'»\)\]]+$/gu,'');
      if (!/[.!?]$/u.test(punctuation)) return false;
      // A numbered heading such as “1. Information We Collect” is one title.
      if (!/\p{L}/u.test(punctuation)) return false;
      const token = punctuation.match(/([^\s]+)\.$/u)?.[1] || '';
      if (/^(?:[\p{L}]\.)+[\p{L}]$/u.test(token)) return false;
      if (/^(?:Mr|Mrs|Ms|Dr|Prof|Sr|Jr|vs|etc|bzw)$/iu.test(token)) return false;
      return index < text.length;
    });
  }

  function readHeading(heading) {
    const nodes = [];
    const breaks = [];
    let text = '';
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (node.nodeType === Node.ELEMENT_NODE && node.matches('svg,script,style,template,[aria-hidden="true"]')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const start = text.length;
        text += node.data;
        nodes.push({node,start,end:text.length});
      } else if (node.tagName === 'BR') breaks.push(text.length);
    }
    return {text,nodes,breaks};
  }

  function alreadyStartsLine(heading, entry, offset) {
    // Keep authored block lines as well as explicit <br> elements.
    for (let parent = entry.node.parentElement; parent && parent !== heading; parent = parent.parentElement) {
      if (!['block','grid','flex','list-item','table'].includes(getComputedStyle(parent).display)) continue;
      const prefix = document.createRange();
      prefix.setStart(parent,0);
      prefix.setEnd(entry.node,offset);
      if (!prefix.toString().trim()) return true;
    }
    return false;
  }

  function insertBreak(heading, node, offset) {
    const br = document.createElement('br');
    br.dataset.bcSentenceBreak = '';
    // Some authored gradients wrap a complete sentence instead of one word.
    // Split that inline-block into sibling spans so the break stays outside it.
    let gradient;
    for (let parent = node.parentElement; parent && parent !== heading; parent = parent.parentElement) {
      if (parent.classList.contains('gradient-word')) gradient = parent;
    }
    if (gradient) {
      const prefix = document.createRange();
      prefix.setStart(gradient,0);
      prefix.setEnd(node,offset);
      if (prefix.toString().trim()) {
        const suffix = document.createRange();
        suffix.setStart(node,offset);
        suffix.setEnd(gradient,gradient.childNodes.length);
        const next = gradient.cloneNode(false);
        next.removeAttribute('id');
        next.appendChild(suffix.extractContents());
        gradient.after(br,next);
        return;
      }
    }
    // Gradient words are inline-blocks. The break belongs outside the word,
    // otherwise it would add height inside that word instead of starting a line.
    if (!node.data.slice(0,offset).trim()) {
      let anchor = node;
      while (anchor.parentElement && anchor.parentElement !== heading) {
        let previous = anchor.previousSibling;
        let hasPrefix = false;
        while (previous) {
          if (previous.nodeType === Node.ELEMENT_NODE || previous.textContent.trim()) { hasPrefix = true; break; }
          previous = previous.previousSibling;
        }
        if (hasPrefix) break;
        anchor = anchor.parentElement;
      }
      anchor.before(br);
    } else {
      const range = document.createRange();
      range.setStart(node,offset);
      range.collapse(true);
      range.insertNode(br);
    }
  }

  function formatHeading(heading) {
    if (!heading.isConnected) return;
    heading.querySelectorAll('br[data-bc-sentence-break]').forEach(br => br.remove());
    const content = readHeading(heading);
    const starts = sentenceStarts(content.text);
    if (!starts.length) {
      delete heading.dataset.bcHeadingPhrases;
      return;
    }
    heading.dataset.bcHeadingPhrases = String(starts.length + 1);
    // Work backwards so splitting a text node leaves earlier offsets intact.
    starts.reverse().forEach(index => {
      const prefixEnd = content.text.slice(0,index).trimEnd().length;
      if (content.breaks.some(position => position >= prefixEnd && position <= index)) return;
      const entry = content.nodes.find(item => item.start <= index && item.end > index);
      if (!entry) return;
      const offset = index - entry.start;
      if (!alreadyStartsLine(heading,entry,offset)) insertBreak(heading,entry.node,offset);
    });
  }

  function collect(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const heading = node.parentElement?.closest(headingSelector);
      if (heading) pending.add(heading);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const heading = node.closest(headingSelector);
    if (heading) pending.add(heading);
    node.querySelectorAll(headingSelector).forEach(item => pending.add(item));
  }

  function flush() {
    frame = null;
    observer.disconnect();
    pending.forEach(formatHeading);
    pending.clear();
    observer.observe(document.body,{subtree:true,childList:true,characterData:true});
  }

  function start() {
    document.querySelectorAll(headingSelector).forEach(formatHeading);
    observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        const targetHeading = (mutation.target.nodeType === Node.TEXT_NODE ? mutation.target.parentElement : mutation.target).closest?.(headingSelector);
        if (targetHeading) pending.add(targetHeading);
        mutation.addedNodes.forEach(collect);
      });
      if (pending.size && !frame) frame = requestAnimationFrame(flush);
    });
    observer.observe(document.body,{subtree:true,childList:true,characterData:true});
  }

  // Run after the existing DOMContentLoaded gradient-word wrapper.
  if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();
